<template>
    <div
        class="p-4 border border-solid"
        :class="{
            'bg-sky-100 border-sky-200 rounded-l-lg rounded-tr-lg rounded-br-sm': props.direction === 'outbound',
            'bg-slate-100  border-slate-200 rounded-r-lg rounded-tl-lg rounded-sm': props.direction === 'inbound',
            hidden: !checked || !loaded,
            'w-9/12 h-9/12': isVideo && !isFileExpired && isMediaSuported,
        }"
    >
        <template v-if="!isFileExpired && isMediaSuported">
            <img
                v-if="isImage"
                class="h-auto rounded-2xl max-w-[200px] max-h-[600px]"
                :src="props.mediaUrl"
                :alt="`message_image`"
                @load="loaded = true"
                @error="onObjectError"
            />
            <video v-else-if="isVideo" controls class="w-full rounded-s" @loadeddata="loaded = true">
                <source :src="props.mediaUrl" :type="props.mediaContentType" @error="onObjectError" />
            </video>
            <audio v-else-if="isAudio" controls @loadeddata="loaded = true">
                <source :src="props.mediaUrl" :type="props.mediaContentType" @error="onObjectError" />
            </audio>
            <div v-else-if="isPdf" class="flex flex-col gap-0.5 items-center justify-between">
                <div>
                    <font-awesome-icon widthAuto v-if="isUseStabIcon" :icon="faFile" size="10x" />
                    <font-awesome-icon widthAuto v-else :icon="faFilePdf" class="text-gray-600" size="5x" />
                </div>
                <a :href="props.mediaUrl" target="_blank" rel="noopener noreferrer">View</a>
            </div>
            <div v-else-if="isVCard" class="flex flex-col items-center justify-between">
                <div>
                    <font-awesome-icon widthAuto v-if="isUseStabIcon" :icon="faFile" size="10x" />
                    <font-awesome-icon widthAuto v-else :icon="faCreditCardFront" class="text-gray-600" size="5x" />
                </div>
                <a :href="props.mediaUrl" target="_blank" rel="noopener noreferrer">View</a>
            </div>
            <media-placeholder
                v-else
                class="text-slate-900"
                @click.stop="emit('mediaPlaceholderClick', { isFileExpired: isFileExpired })"
            >
                <a :href="props.mediaUrl" download target="_blank" rel="noopener noreferrer">Download</a>
            </media-placeholder>
        </template>
        <template v-else>
            <media-placeholder
                class="text-slate-900"
                @click.stop="emit('mediaPlaceholderClick', { isFileExpired: isFileExpired })"
            >
                <template v-if="isFileExpired">Media Expired</template>
                <template v-else>
                    <a :href="props.mediaUrl" download target="_blank" rel="noopener noreferrer">Download</a>
                </template>
            </media-placeholder>
        </template>
    </div>
</template>

<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import MediaPlaceholder from './MediaPlaceholder.vue';
import { ref, computed, onMounted } from 'vue';
import { faFile, faFilePdf } from '@fortawesome/pro-regular-svg-icons';
import { faCreditCardFront } from '@fortawesome/pro-solid-svg-icons';

const props = defineProps({
    mediaUrl: {
        type: String,
        requried: true,
    },
    mediaContentType: {
        type: String,
        requried: true,
    },
    isUseStabIcon: {
        type: Boolean,
        default: false,
    },
    direction: {
        type: String,
        required: true,
    },
});

const emit = defineEmits(['mediaPlaceholderClick']);

const isImage = computed(() =>
    Boolean(
        props.mediaUrl?.toLowerCase()?.match(/\.(png|jpg|jpeg|gif|bmp)$/) ||
            (props.mediaContentType || '').toLowerCase().includes('image')
    )
);
const isVideo = computed(() =>
    Boolean(
        props.mediaUrl?.toLowerCase().match(/\.(mp4|webm)$/) ||
            (props.mediaContentType || '').toLowerCase().includes('video')
    )
);
const isAudio = computed(() =>
    Boolean(
        props.mediaUrl?.toLowerCase().match(/\.(mp3|mpeg|ogg|wav)$/) ||
            (props.mediaContentType || '').toLowerCase().includes('audio')
    )
);
const isPdf = computed(() =>
    Boolean(
        props.mediaUrl?.toLowerCase().match(/\.(pdf)$/) || (props.mediaContentType || '').toLowerCase().includes('pdf')
    )
);
const isVCard = computed(() =>
    Boolean(
        props.mediaUrl?.toLowerCase().match(/\.(vcf|vcard)$/) ||
            (props.mediaContentType || '').toLowerCase().includes('vcard')
    )
);

const isMediaSuported = ref(true);
const isFileExpired = ref(false);
const checked = ref(false);
const loaded = ref(false);

const checkMedia = async () => {
    try {
        const res = await fetch(props.mediaUrl);
        if (!res.ok) {
            isFileExpired.value = true;
            loaded.value = true;
        }
    } catch (error) {
        console.error(error);

        isFileExpired.value = true;
        loaded.value = true;
    } finally {
        checked.value = true;

        if (!isImage.value && !isVideo.value && !isAudio.value) {
            loaded.value = true;
        }
    }
};

const onObjectError = async () => {
    try {
        const res = await fetch(props.mediaUrl);
        if (res.ok) {
            isMediaSuported.value = false;
        } else {
            isFileExpired.value = true;
        }
    } catch (error) {
        console.error(error);
    } finally {
        loaded.value = true;
    }
};

onMounted(() => {
    checkMedia();
});
</script>
